import React, { useMemo, useState } from 'react';
import { Content, Header } from '@backstage/core-components';
import { Box, Tabs, Button } from '@material-ui/core';
import { StyledChip, StyledTab } from './UserRequestsStyles';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useUserRequestsContext } from './UserRequestsContext';
import { useUserRequestsPermissions } from './hooks';
import { FlexBox } from '../global/FlexBox';
import WhitelistDrawer from './RegistrationRequests/WhitelistDrawer/WhitelistDrawer';
import { PageTitle } from '../../shared/components';

const TabLabel: React.FC<{ text: string; count?: number }> = ({
  text,
  count,
}) => (
  <span style={{ display: 'flex', alignItems: 'center' }}>
    <span>{text}</span>
    {!!count && count > 0 && <StyledChip label={count} size="small" />}
  </span>
);

interface TabConfig {
  route: string;
  label: string;
  counterKey: string;
}

const ALL_TABS: TabConfig[] = [
  {
    route: 'registration',
    label: 'Registration Requests',
    counterKey: 'registration',
  },
  { route: 'support', label: 'Support Requests', counterKey: 'support' },
  { route: 'intake', label: 'Intake Requests', counterKey: 'intake' },
];

const UserRequestsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { counters } = useUserRequestsContext();
  const { permissions } = useUserRequestsPermissions();

  const [isWhitelistOpen, setIsWhitelistOpen] = useState(false);
  const canWriteWhitelist = !!permissions.whitelist.write;
  const isWhitelistEnabled = !!permissions.whitelist.read || canWriteWhitelist;

  const visibleTabs = useMemo(
    () =>
      ALL_TABS.filter(tab => {
        const perm = permissions[tab.route as keyof typeof permissions];
        return perm?.read || perm?.write;
      }),
    [permissions],
  );

  const currentTab = visibleTabs.findIndex(tab =>
    location.pathname.includes(tab.route),
  );

  const isRegistrationPage = currentTab === 0;

  const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    navigate(visibleTabs[newValue].route);
  };

  if (visibleTabs.length === 0) {
    return null;
  }

  if (currentTab === -1) {
    return <Navigate to={visibleTabs[0].route} replace />;
  }

  return (
    <>
      <Header
        pageTitleOverride="User Requests"
        title={
          <FlexBox
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <PageTitle>User Requests</PageTitle>
            <FlexBox sx={{ gridColumnGap: 8, justifyContent: 'flex-end' }}>
              {isRegistrationPage && isWhitelistEnabled && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setIsWhitelistOpen(true)}
                >
                  Manage Whitelist
                </Button>
              )}
            </FlexBox>
          </FlexBox>
        }
      />
      <Content>
        <Tabs
          textColor="primary"
          indicatorColor="primary"
          value={currentTab}
          onChange={handleChange}
        >
          {visibleTabs.map(tab => (
            <StyledTab
              key={tab.route}
              label={
                <TabLabel text={tab.label} count={counters[tab.counterKey]} />
              }
            />
          ))}
        </Tabs>
        <Box sx={{ mt: 4 }}>
          <Outlet />
        </Box>
      </Content>
      <WhitelistDrawer
        isOpen={isWhitelistOpen}
        onClose={() => setIsWhitelistOpen(false)}
        readOnly={!canWriteWhitelist}
      />
    </>
  );
};

export default UserRequestsLayout;
